<template>
  <content-subpage
    v-if="isReady"
    class="search-resource-detail-content-subpage"
    :visible="visible"
    :has-parent-subpage="hasParentSubpage"
    :titles="titles"
    :show-content-title="false"
    :back-label="backLabel"
    @content-subpage-hidden="actionHidden(handleHidden())"
    @content-subpage-back="actionBack(handleBack())"
  >
    <template #title-buttons>
      <star-resource-button
        v-if="showStarResource"
        :resource-uuid="paramsData.uuid"
        :resource-type="paramsData.type"
        :resource-status="getStatus"
        @resource-unstarred="handleResourceUnstarred"
      />
    </template>
    <template #main-content>
      <search-rubric-resource-preview
        v-if="isRubricResourceType"
        :rubric-data="paramsData.gradingRubric"
      />
      <search-resource-preview v-else :params="paramsData" :show-notes="dataShowNotes" />
    </template>
    <template #sub-content>
      <search-course-resource-detail
        v-if="isCourseResourceType"
        v-model:selection-mode="selectionMode"
        :params="paramsData"
        context="contentSubpage"
        @anchor="handleAnchor"
        @activities-selected="handleActivitiesSelected"
        @sections-selected="handleSectionsSelected"
        @game-book-selected="handleGameBookSelected"
        @toggle-notes="handleNotesToggle"
      />
      <search-media-resource-detail
        v-else-if="isMOResourceType"
        :params="paramsData"
        context="contentSubpage"
        @activities-selected="handleActivitiesSelected"
      />
      <search-rubric-resource-detail
        v-else-if="isRubricResourceType"
        v-model:selection-mode="selectionMode"
        :rubric-data="paramsData.gradingRubric"
        context="contentSubpage"
        @anchor="handleAnchor"
      />
      <search-resource-detail
        v-else
        :params="paramsData"
        context="contentSubpage"
        @activities-selected="handleActivitiesSelected"
        @current-question-changed="handleCurrentQuestionChanged"
      />
    </template>
    <template v-if="showActionButton" #action-button>
      <fetch-data-button
        v-if="showChooseButton"
        v-bind="dataSaveButton"
        :loading="getProcessingTriggerElement === 'save'"
        :disabled="isChooseButtonDisabled"
        :icon-only="false"
        tip-position="top"
        tip-alignment="end"
        @click="handleChoose"
        @reset-completed-state="handleSaveButtonResetCompleted"
        @reset-error-state="handleSaveButtonResetError"
      >
        <template v-if="dataSaveButton.error">
          {{ $t("button_state_error") }}
        </template>
        <template v-else>
          <svg-icon name="Proceed32" kind="action" />
          <span>{{ $t("search_lesson_preview_choose_activity") }}</span>
        </template>
      </fetch-data-button>
      <fetch-data-button
        v-if="showEditButton"
        v-bind="dataEditButton"
        :loading="getProcessingTriggerElement === 'edit'"
        :disabled="isEditButtonDisabled"
        :icon-only="false"
        @click="handleEdit"
        @reset-error-state="handleEditButtonResetError"
      >
        <template v-if="dataEditButton.error">
          <span>{{ $t("button_state_error") }}</span>
        </template>
        <template v-else>
          <svg-icon name="Pencil32" kind="action" />
          <span>{{ $t("edit_label") }}</span>
        </template>
      </fetch-data-button>
    </template>
  </content-subpage>
</template>

<script lang="ts">
import { isEmpty } from "@utils/var.check";
import {
  RESOURCE_TYPE,
  QUESTION_TYPE,
  RESOURCE_DOMAIN,
  COMPONENT_AUTHORING_COPILOT_TYPE,
} from "@modules/editor/service/editor.constant";
import ContentSubpageMixin from "@plugins/content-subpage/mixin/content.subpage.mixin";
import ProcessingStateMixin from "@modules/app/mixin/processing.state.mixin";
import ErrorMixin from "@plugins/common/mixin/error.mixin";
import SearchResourceDetail from "@modules/search/components/SearchResourceDetail.vue";
import SearchCourseResourceDetail from "@modules/search/components/SearchCourseResourceDetail.vue";
import SearchMediaResourceDetail from "@modules/search/components/SearchMediaResourceDetail.vue";
import SearchRubricResourceDetail from "@modules/search/components/SearchRubricResourceDetail.vue";
import SearchResourcePreview from "@modules/search/components/SearchResourcePreview.vue";
import SearchRubricResourcePreview from "@modules/search/components/SearchRubricResourcePreview.vue";
import lessonApi from "@apis/editor.lesson.api";
import assignmentLessonApi from "@apis/assignment.lesson.api";
import questionApi from "@apis/question.api";
import mediaObjectApi from "@apis/media.object.api";
import GradingRubricApi from "@/apis/grading.rubric.api";
import SearchStore from "@modules/search/components/SearchStore";
import QuizActivityStore from "@modules/editor/components/quiz-activity/QuizActivityStore.ts";
import { RESOURCE_STATUS, RESOURCE_TYPE_VALUE } from "@modules/search/service/search.constant";
import GoogleMixin from "@plugins/google/google.mixin";
import { MEDIA_TYPE } from "@plugins/media/media.constant";
import EditorTemplateResourceSelectionMixin from "@modules/editor/mixin/editor.template.resource.selection.mixin";
import { FROM_LOCATION_TYPE } from "@modules/assignment/service/assignment.constant";
import { RESOURCE_DOMAIN_PATH } from "@plugins/common/constant/resource.constant";
import CGLessonApi from "@/apis/cg.lesson.api";
import ResourceEditMixin from "@plugins/resource/mixin/resource.edit.mixin";
import { mapGetters } from "vuex";
import { NAMESPACE } from "@modules/print-view/service/print.view.constant";
import { LESSON_ACTIVITY_TYPE } from "@plugins/common/constant/resource.constant";
import AuthoringCopilotTemplateSelectionMixin from "@modules/editor/mixin/authoring.copilot.template.selection.mixin";
import ResourceMixin from "@plugins/common/mixin/resource.mixin";
import PageDirtyMixin from "@modules/app/mixin/page.dirty.mixin";
import moeLessonApi from "@apis/moe.lesson.api";
import ScrollUtils from "@utils/scroll.utils";
import DateTimeFormatter from "@utils/datetime.formatter.ts";
import algebraKitApi from "@/apis/algebraKit.api";

export default {
  name: "SearchResourceDetailContentSubpage",
  components: {
    SearchResourcePreview,
    SearchResourceDetail,
    SearchCourseResourceDetail,
    SearchMediaResourceDetail,
    SearchRubricResourceDetail,
    SearchRubricResourcePreview,
  },
  mixins: [
    ResourceMixin,
    ContentSubpageMixin,
    ProcessingStateMixin,
    ErrorMixin,
    GoogleMixin,
    EditorTemplateResourceSelectionMixin,
    ResourceEditMixin,
    AuthoringCopilotTemplateSelectionMixin,
    PageDirtyMixin,
  ],
  provide() {
    return {
      richTextContext: {
        isStarredResource: this.params.isStarredResource,
        lessonUuid: this.isCommunityResource ? this.params.resourceUuid : this.params.uuid,
        getStarredResourceLessonUuid: () => {
          return this.starredQuestionLessonUuid;
        },
        whitelistDomainMaps: this.getWhitelistDomainMaps,
      },
      getAlgebrakitSpec: () => this.algebrakitSpec,
      getAlgebrakitSessionId: () => this.algebrakitSessionId,
    };
  },
  props: {
    params: { type: Object, default: null },
  },
  emits: ["templateSelected", "resourceSelected", "resourceUnstarred"],
  data() {
    return {
      visible: true,
      parentSubpageName: "",
      isReady: false,
      searchStore: SearchStore,
      store: QuizActivityStore,
      selectedActivities: [],
      selectedGameBookId: null,
      resourceUnstarred: false,
      dataShowNotes: false,
      paramsData: { ...this.params },
      dataEditButton: {
        kind: "primary",
        size: "small",
        loading: false,
        error: false,
        errorMessage: "",
        tipPosition: "top",
        tipAlignment: "end",
      },
      starredQuestionLessonUuid: "",
      algebrakitSpec: null,
      algebrakitSessionId: null,
    };
  },
  computed: {
    ...mapGetters(NAMESPACE.APP, ["getWhitelistDomainMaps"]),
    isSelectingResource() {
      return this.paramsData.isContextCreateAssignment || this.paramsData.isAttachingAttachment;
    },
    showActionButton() {
      return this.showChooseButton || this.showEditButton;
    },
    showChooseButton() {
      return this.paramsData.hasSelectAction || this.isSelectingResource;
    },
    isFromGamification() {
      return this.paramsData.isFromGamification;
    },
    showEditButton() {
      return this.paramsData?.hasEditAction ?? false;
    },
    processingId() {
      return this.params.uuid;
    },
    backLabel() {
      return `${this.$i18n.t("content_subpage_go_back_link")} ${this.parentSubpageName}`;
    },
    dirtyId() {
      return this.paramsData.uuid;
    },
    isMpqQuestionPartNotSelected() {
      return (
        this.isAddingFromQuestionParts && isEmpty(this.searchStore.getQuestionPartsSelectionList())
      );
    },
    isChooseButtonDisabled() {
      if (this.dataSaveButton.loading || this.dataSaveButton.error) {
        return true;
      } else if (this.isFromLessonModuleAuthoringCopilot) {
        return false;
      } else if (this.isFromLessonActivityAuthoringCopilot) {
        return isEmpty(this.sectionsSelected);
      } else if (this.isFromLessonComponentAuthoringCopilot) {
        return isEmpty(this.activitiesSelected);
      }

      if (this.isFromGamification) {
        return isEmpty(this.selectedGameBookId);
      }

      if (this.isMpqQuestionPartNotSelected) {
        return true;
      }

      if (this.isSelectingResource) {
        return this.isProcessing;
      }

      if (this.paramsData.type === RESOURCE_TYPE.LESSON) {
        if (this.isDefaultSelectionMode && this.activitiesSelected.length === 0) {
          return true;
        }
        return !this.isDirty(this.dirtyId) || this.isProcessing;
      }
      return false;
    },
    isEditButtonDisabled() {
      return this.dataEditButton.error;
    },
    isCourseResourceType() {
      return (
        this.paramsData.type === RESOURCE_TYPE.LESSON ||
        this.paramsData.type === "ASSIGNMENT_LESSON"
      );
    },
    isMOResourceType() {
      return this.paramsData.type === RESOURCE_TYPE.MEDIA;
    },
    isRubricResourceType() {
      return this.paramsData.type === RESOURCE_TYPE.RUBRIC;
    },
    isCommunityResource() {
      return this.paramsData.resourceDomain === RESOURCE_DOMAIN_PATH.COMMUNITY_GALLERY;
    },
    isCreatingMOEResource() {
      return this.params.ownerGroupId != null;
    },
    getStatus() {
      return this.paramsData.status ? this.paramsData.status : RESOURCE_STATUS.APPROVED;
    },
    showStarResource() {
      if (this.paramsData.type === "ASSIGNMENT_LESSON") {
        return false;
      }

      if (this.isAlpResource) {
        return false;
      }
      if (this.paramsData.type === RESOURCE_TYPE.MEDIA) {
        //only approved media type can be starred
        return this.getStatus === RESOURCE_STATUS.APPROVED;
      }

      if (this.isCommunityResource) {
        return false;
      }
      return true;
    },
    isAlpResource() {
      return this.paramsData.lesson
        ? this.paramsData.lesson.resourceDomain === RESOURCE_DOMAIN.ALP_TEMPLATE.value
        : false;
    },
    isClassGroupResource() {
      return this.paramsData.location === this.$i18n.t("class_group_label");
    },
    isMpqQuestion() {
      return this.paramsData.question.questionType === QUESTION_TYPE.MPQ;
    },
    isMpqTopParentQuestion() {
      return this.isMpqQuestion && this.paramsData.question.topParentQuestionUuid === null;
    },
    isMpqChildQuestion() {
      return (
        this.paramsData.question.topParentQuestionUuid !== null &&
        this.paramsData.question.topParentQuestionUuid !== this.paramsData.question.uuid
      );
    },
    isFRQWithAk() {
      return (
        !this.paramsData.question?.topParentQuestionUuid &&
        !!this.paramsData.question?.algebrakitRefId &&
        !!this.paramsData.question?.algebrakitSpec
      );
    },
    isAddingFromQuestionParts() {
      // SearchResourceDetailQuestionParts
      return (
        this.paramsData.type === RESOURCE_TYPE.QUESTION &&
        (this.isMpqQuestion || this.isMpqChildQuestion)
      );
    },
    isAllowedEdit() {
      return this.paramsData.lesson.allowedEdit;
    },
    fromLocation() {
      const routeName = this.$route.name;
      switch (routeName) {
        case "ManageAlpTemplate":
          return FROM_LOCATION_TYPE.ALP_TEMPLATE;
        case "MyDrive":
          return FROM_LOCATION_TYPE.MY_DRIVE;
        case "StudentGroup":
          return FROM_LOCATION_TYPE.STUDENT_GROUP;
        case "Planner":
          return FROM_LOCATION_TYPE.CALENDAR;
        default:
          return FROM_LOCATION_TYPE.HOME;
      }
    },
  },
  created() {
    if (this.hasParentSubpage) {
      this.parentSubpageName = this.titles.slice(-1)[0]
        ? this.titles.slice(-1)[0]
        : this.paramsData.title;
    }
    if (this.paramsData.addFromLocation) {
      this.titles.push(this.paramsData.title);
    } else {
      // SLSSIT-5371, SLS-7302
      // Show only title for Global Search, My Drive listing
      this.titles.push(this.paramsData.title);
    }

    const fetchDetailsRequestObject = {
      uuid: this.paramsData.uuid,
    };
    if (this.isFromLessonComponentAuthoringCopilot || this.isFromLessonActivityAuthoringCopilot) {
      fetchDetailsRequestObject.additionalDetails = true;
    }
    switch (this.paramsData.type) {
      case "LESSON":
        this.titles.push(this.$i18n.t("search_course_detail_subpage_title"));
        if (this.isCommunityResource) {
          CGLessonApi.fetchCGDetails(fetchDetailsRequestObject).then((response) => {
            if (response) {
              this.paramsData.lesson = response.data.formData;
              this.paramsData.communityResourceUuid = response.data.communityResourceUuid;
              this.isReady = true;
            }
          }, this.passiveApiFailHandler);
        } else if (this.paramsData.admin && this.paramsData.admin === "admin") {
          lessonApi.fetchManageLessonDetails(fetchDetailsRequestObject).then((response) => {
            if (response) {
              this.paramsData.lesson = response.data.formData;
              this.paramsData.communityResourceUuid = response.data.communityResourceUuid;
              this.isReady = true;
            }
          }, this.passiveApiFailHandler);
        } else {
          lessonApi.fetchLessonDetails(fetchDetailsRequestObject).then((response) => {
            if (response) {
              if (response.code !== "ok") {
                this.passiveApiFailHandler();
                return;
              }
              this.paramsData.lesson = response.data.formData;
              this.paramsData.communityResourceUuid = response.data.communityResourceUuid;
              this.isReady = true;
            }
          }, this.passiveApiFailHandler);
        }
        break;
      case "ASSIGNMENT_LESSON":
        this.titles.push(this.$i18n.t("search_resource_detail_subpage_title"));
        assignmentLessonApi
          .fetchAssignmentLesson({
            uuid: this.paramsData.uuid,
            assignmentUuid: this.params.assignmentUuid,
          })
          .then((response) => {
            if (response) {
              this.paramsData.lesson = response.data;
              this.isReady = true;
            }
          }, this.passiveApiFailHandler);
        break;
      case "QUESTION":
        this.titles.push(this.$i18n.t("search_resource_detail_question_details_label"));
        questionApi.fetchQuestion(this.paramsData.uuid).then((response) => {
          //OK
          if (response) {
            this.paramsData.question = response.data;
            const promiseArray: Promise<void>[] = [];

            // Fetch mpq questions
            if ((this.isMpqQuestion && !this.isMpqTopParentQuestion) || this.isMpqChildQuestion) {
              const question = this.paramsData.question;
              const uuid = question.topParentQuestionUuid;

              const fetchMpqQuestionsPromise: Promise<void> = (async () => {
                const mpqResponse = await questionApi.fetchQuestion(uuid);
                if (mpqResponse) {
                  this.paramsData.mpqQuestion = mpqResponse.data;
                  await this.initAlgebrakitSession(question);
                }
              })().catch(this.passiveApiFailHandler);

              promiseArray.push(fetchMpqQuestionsPromise);
            } else if (this.isMpqTopParentQuestion) {
              this.paramsData.mpqQuestion = response.data;
            }

            // Standalone FREE + AK
            if (this.isFRQWithAk) {
              const initAkPromise = this.initAlgebrakitSession(response.data);
              promiseArray.push(initAkPromise);
            }

            // Fetch grading rubric
            if (!isEmpty(response.data.gradingRubricUuid)) {
              const fetchGradingRubricPromise = GradingRubricApi.fetchGradingRubric(
                response.data.gradingRubricUuid,
              );
              fetchGradingRubricPromise.then(
                //OK
                (response) => {
                  if (response) {
                    this.paramsData.gradingRubric = response.data;
                  }
                },
                this.passiveApiFailHandler, //OK
              );

              promiseArray.push(fetchGradingRubricPromise);
            }

            Promise.all(promiseArray).then(() => {
              this.isReady = true;
            });
          }
        }, this.passiveApiFailHandler); //OK
        break;
      case "RUBRIC":
        GradingRubricApi.fetchGradingRubric(this.params.uuid).then((response) => {
          if (response) {
            if (response.code !== "ok") {
              this.passiveApiFailHandler();
              return;
            }
            if (response.data) {
              this.paramsData.gradingRubric = this.processRubricResponse(response);
              this.isReady = true;
            }
          }
        }, this.passiveApiFailHandler);
        break;
      case "MEDIA":
        if (this.paramsData.admin && this.paramsData.admin === "admin") {
          const metadataPromise = mediaObjectApi.getMetaData({
            uuid: this.paramsData.uuid,
          });
          const copyRightPromise = mediaObjectApi.fetchMediaObjectCopyrightData(
            this.paramsData.uuid,
          );

          Promise.all([metadataPromise, copyRightPromise]).then((response) => {
            //OK
            if (response[0].code === "ok") {
              this.paramsData.mediaObject = response[0].data;
            }
            if (response[1].code === "ok") {
              this.paramsData.copyright = response[1].data;
            }
            this.titles.push(
              this.getHeaderLabel(this.paramsData.mediaObject.fileType.toUpperCase()),
            );
            this.isReady = true;
          }, this.passiveApiFailHandler); //OK
        } else {
          mediaObjectApi
            .getMetaData({
              uuid: this.paramsData.uuid,
            })
            .then((response) => {
              //OK
              if (response) {
                this.paramsData.mediaObject = response.data;
                this.titles.push(
                  this.getHeaderLabel(this.paramsData.mediaObject.fileType.toUpperCase()),
                );
                this.isReady = true;
              }
            }, this.passiveApiFailHandler); //OK
        }

        break;
      default:
        this.isReady = true;
        break;
    }
  },
  unmounted() {
    this.resetProcessing();
    // remove title if subpage is closed
    this.titles.pop();
  },
  methods: {
    async initAlgebrakitSession(question): Promise<void> {
      this.algebrakitSpec = null;
      this.algebrakitSessionId = null;

      const isMpqChildWithAk =
        question.topParentQuestionUuid !== null &&
        question.topParentQuestionUuid !== question.uuid &&
        !!question.algebrakitRefId &&
        !question.algebrakitSpec;

      const isFRQWithAk =
        !question.topParentQuestionUuid && !!question.algebrakitRefId && !!question.algebrakitSpec;

      if (isMpqChildWithAk) {
        const parentSpecString = this.paramsData.mpqQuestion?.algebrakitSpec;
        if (parentSpecString) {
          await this.createAlgebrakitSession(parentSpecString);
        }
      } else if (isFRQWithAk && question.algebrakitSpec) {
        await this.createAlgebrakitSession(question.algebrakitSpec);
      }
    },
    async handleCurrentQuestionChanged(question) {
      const data = this.paramsData;
      data.uuid = question.uuid;
      data.title = question.questionBodyText;

      await this.initAlgebrakitSession(question);

      data.question = question;

      if (!isEmpty(question.gradingRubricUuid)) {
        GradingRubricApi.fetchGradingRubric(question.gradingRubricUuid).then((response) => {
          if (response) {
            data.gradingRubric = response.data;
            this.paramsData = { ...data };
          }
        }, this.passiveApiFailHandler);
      } else {
        data.gradingRubric = undefined;
        this.paramsData = { ...data };
      }

      // Index of question title is before the last item in the array -- Question Details, so minus 2
      this.titles[this.titles.length - 2] = data.title;
    },
    async createAlgebrakitSession(specString: string) {
      try {
        const spec = JSON.parse(specString);
        const sessionResponse = await algebraKitApi.createSession(
          JSON.stringify({ exercises: [{ exerciseSpec: spec }] }),
        );
        const sessionId = sessionResponse?.[0]?.[0]?.sessionId;
        if (sessionId) {
          this.algebrakitSpec = spec;
          this.algebrakitSessionId = sessionId;
        }
      } catch (error) {
        console.error(error);
        this.algebrakitSpec = null;
        this.algebrakitSessionId = null;
      }
    },
    handleGameBookSelected(gameBookId) {
      this.selectedGameBookId = gameBookId;
      if (gameBookId) {
        this.removeDirty(this.dirtyId);
        return;
      }
      this.setDirty(this.dirtyId);
    },
    handleChoose() {
      if (this.isDisabled) return;

      if (this.isFromAuthoringCopilot) {
        let templateSelectedInfo;
        if (this.isFromLessonModuleAuthoringCopilot) {
          templateSelectedInfo = {
            templateUuid: this.paramsData.templateUuid,
            referenceLessonUuid: this.paramsData.lesson.uuid,
            referenceLessonLocation: this.location(this.paramsData.lesson.resourceDomain),
            referenceLessonId: this.paramsData.lesson.id,
            referenceLessonTitle: this.paramsData.lesson.title,
            numOfSections: this.getNumOfSections(),
          };
        } else if (this.isFromLessonActivityAuthoringCopilot) {
          const sectionId = Number(this.sectionsSelected[0]);
          const section = this.paramsData.lesson.lessonActivityList.find(
            (lessonActivity) =>
              lessonActivity.type === LESSON_ACTIVITY_TYPE.SECTION &&
              lessonActivity.id === sectionId,
          );
          templateSelectedInfo = {
            templateUuid: this.paramsData.templateUuid,
            referenceLessonUuid: this.paramsData.lesson.uuid,
            referenceLessonLocation: this.location(this.paramsData.lesson.resourceDomain),
            numOfActivities: section.childrenActivityCount ?? 0,
            referenceLessonActivityId: sectionId,
            referenceLessonActivityTitle: section.title,
            pageNo: section.pageNo,
            hideSectionActivityNumber: this.paramsData.lesson.hideSectionActivityNumber,
            showSectionCover: this.paramsData.lesson.showSectionCover,
          };
        } else {
          if (this.params.activityType === COMPONENT_AUTHORING_COPILOT_TYPE.ACTIVITY) {
            const activityId = Number(this.activitiesSelected[0]);
            const activity = this.paramsData.lesson.lessonActivityList.find(
              (lessonActivity) =>
                lessonActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY &&
                lessonActivity.id === activityId,
            );

            templateSelectedInfo = {
              templateUuid: this.paramsData.templateUuid,
              referenceLessonUuid: this.paramsData.lesson.uuid,
              referenceLessonLocation: this.location(this.paramsData.lesson.resourceDomain),
              numOfComponents: activity.aiEligibleComponentCount ?? 0,
              referenceLessonActivityId: activityId,
              referenceLessonActivityTitle: activity.title,
              alpStage: activity.alpStage.name,
              alpStageDisplayName: activity.alpStage.displayName,
              pageNo: activity.pageNo,
              optional: activity.optional,
              type: activity.type,
              hideSectionActivityNumber: this.paramsData.lesson.hideSectionActivityNumber,
            };
          } else {
            const activityQuizId = Number(this.activitiesSelected[0]);
            const activityQuiz = this.paramsData.lesson.lessonActivityList.find(
              (lessonActivity) =>
                lessonActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ &&
                lessonActivity.id === activityQuizId,
            );

            templateSelectedInfo = {
              templateUuid: this.paramsData.templateUuid,
              referenceLessonUuid: this.paramsData.lesson.uuid,
              referenceLessonLocation: this.location(this.paramsData.lesson.resourceDomain),
              numOfComponents: activityQuiz.aiEligibleComponentCount ?? 0,
              referenceLessonActivityId: activityQuizId,
              referenceLessonActivityTitle: activityQuiz.title,
              alpStage: activityQuiz.alpStage.name,
              alpStageDisplayName: activityQuiz.alpStage.displayName,
              pageNo: activityQuiz.pageNo,
              optional: activityQuiz.optional,
              type: activityQuiz.type,
              hideSectionActivityNumber: this.paramsData.lesson.hideSectionActivityNumber,
            };
          }
        }
        this.$emit("templateSelected", templateSelectedInfo);
        this.handleSuccessCallback();
        return;
      }

      if (this.isFromGamification) {
        this.handleGameBookChosen();
        return;
      }

      if (this.paramsData.type === "ASSIGNMENT_LESSON") {
        this.handleResourceSelected();
        return;
      }

      switch (this.paramsData.type) {
        case RESOURCE_TYPE.LESSON:
          this.checkIfHasGoogleDriveAccess(
            this.showAppAvailabilityWarningModel,
            this.handleChoosePrimaryAction(),
            () => {
              // empty
            },
            {
              requestData: [
                { uuid: this.paramsData.lesson.uuid, status: this.paramsData.lesson.status },
              ],
              googleDocumentRootFolderType: "LESSON",
            },
          );

          break;
        case RESOURCE_TYPE.QUESTION:
        case RESOURCE_TYPE.RUBRIC:
        case RESOURCE_TYPE.MEDIA:
        case RESOURCE_TYPE.QUIZ:
          this.setProcessing("save");
          this.$emit("resourceSelected", {
            data: { uuid: this.paramsData.uuid },
            onSuccessCallback: this.handleSuccessCallback,
            onFailCallback: this.handleFailCallback,
            isAddingFromQuestionParts: this.isAddingFromQuestionParts,
          });
          break;
        default:
          break;
      }
    },
    handleChoosePrimaryAction() {
      if (this.isSelectingResource) {
        return this.handleResourceSelected;
      }

      this.setProcessing("save");
      this.removeDirty(this.dirtyId);
      if (this.inLessonEditor) {
        return this.addSectionsOrActivities;
      }
      return this.createLesson;
    },
    handleGameBookChosen() {
      if (!this.isFromGamification) {
        return;
      }
      this.resetProcessing();
      this.removeDirty(this.dirtyId);
      this.$emit("resourceSelected", this.selectedGameBookId);
    },
    handleResourceSelected() {
      this.$emit("resourceSelected");
      if (this.paramsData.isAttachingAttachment) {
        this.actionBack();
      }
    },
    getNumOfSections() {
      const lessonActivityList = this.paramsData.lesson.lessonActivityList;
      let sectionCount = 0;
      for (const lessonActivity of lessonActivityList) {
        if (lessonActivity.type === LESSON_ACTIVITY_TYPE.SECTION) sectionCount++;
      }
      return sectionCount;
    },
    async createLesson() {
      try {
        const response = this.isCreatingMOEResource
          ? await this.createMOELessonApi()
          : await this.createLessonApi();
        if (!response) {
          // case when user cancel any of the prompts done in createLessonApi
          this.handleResetProcessing();
          return;
        }
        if (response.code !== "ok") {
          return this.passiveApiFailHandler();
        }

        const newLessonUuid = response.data;
        this.handleSuccessCallback();
        const params = this.isCreatingMOEResource
          ? {
              resourceDomain: RESOURCE_DOMAIN_PATH.MOE_LIBRARY,
              lessonUuid: newLessonUuid, //TODO [SLS-7201] do replace with moeLibraryUuid
              mode: "edit",
              admin: "admin",
              fromLocation: this.fromLocation,
            }
          : {
              resourceDomain: RESOURCE_DOMAIN_PATH.MY_LIBRARY,
              lessonUuid: newLessonUuid,
              mode: "edit",
              fromLocation: this.fromLocation,
            };
        this.$router.push({
          name: "EditorCover",
          params: params,
          query: {
            version: this.isCreatingMOEResource ? newLessonUuid : undefined,
            isNavigateToFirstActivity: false,
            showLessonPlan: undefined,
          },
        });
        this.resourceEditMixin_handleSaveLessonEditHistory(newLessonUuid);
      } catch (e) {
        return Promise.reject(e);
      }
    },
    createLessonApi() {
      return new Promise((resolve, reject) => {
        lessonApi
          .createLessonWithSourceLesson(
            this.paramsData.uuid,
            this.paramsData.folderUuid,
            isEmpty(this.sectionsSelected) ? this.activitiesSelected : [],
            this.paramsData.lesson.showSectionCover ? this.sectionsSelected : [],
          )
          .then(resolve, reject);
      });
    },
    createMOELessonApi() {
      return new Promise((resolve, reject) => {
        moeLessonApi
          .createMOELessonWithSourceLesson(
            this.paramsData.uuid,
            isEmpty(this.sectionsSelected) ? this.activitiesSelected : [],
            this.paramsData.lesson.showSectionCover ? this.sectionsSelected : [],
            this.params.ownerGroupId,
          )
          .then(resolve, reject);
      });
    },
    handleSuccessCallback() {
      this.resetProcessing();
      this.removeDirty(this.dirtyId);
      this.dataSaveButton.completed = true;
      this.$nextTick(() => {
        this.actionBack();
      });
    },
    handleFailCallback() {
      this.resetProcessing();
      this.dataSaveButton.error = true;
    },
    handleResetProcessing() {
      this.resetProcessing();
    },
    handleSaveError(isValidationError) {
      this.resetProcessing();
      this.dataSaveButton.error = true;
      this.dataSaveButton.errorMessage = isValidationError
        ? this.$i18n.t("search_lesson_preview_maximum_activities_error")
        : this.$i18n.t("api_action_generic_error_message");
    },
    handleResourceUnstarred(resourceUnstarred) {
      this.resourceUnstarred = resourceUnstarred;
    },
    handleHidden() {
      if (this.resourceUnstarred) {
        this.$emit("resourceUnstarred");
      }
    },
    handleBack() {
      if (
        typeof this.paramsData.actionBack !== "undefined" &&
        typeof this.paramsData.actionBack === "function"
      ) {
        this.paramsData.actionBack();
      }

      if (this.resourceUnstarred) {
        this.$emit("resourceUnstarred");
      }
    },
    getHeaderLabel(type) {
      switch (type) {
        case MEDIA_TYPE.HTML5:
          return this.$i18n.t("media_settings_interactable_settings");
        case MEDIA_TYPE.IMAGE:
          return this.$i18n.t("search_resource_detail_image_details_label");
        case MEDIA_TYPE.VIDEO:
          return this.$i18n.t("search_resource_detail_video_details_label");
        case MEDIA_TYPE.AUDIO:
          return this.$i18n.t("search_resource_detail_audio_details_label");
        case MEDIA_TYPE.POWERPOINT:
        case MEDIA_TYPE.WORD:
        case MEDIA_TYPE.EXCEL:
        case MEDIA_TYPE.PDF:
          return this.$i18n.t("search_resource_detail_document_details_label");
        case MEDIA_TYPE.ZIP:
        case MEDIA_TYPE.OTHERS:
          return this.$i18n.t("search_resource_detail_others_details_label");
        default:
          return "-";
      }
    },
    handleNotesToggle(value) {
      this.dataShowNotes = value;
    },
    handleResponseCallback(response) {
      this.resetProcessing();
      if (!isEmpty(response)) {
        this.paramsData.lesson.paused = response;
      }
    },
    onFail() {
      this.dataSaveButton.error = true;
      this.resetProcessing();
      this.dataSaveButton.loading = false;
    },
    onEditFail() {
      this.dataEditButton.error = true;
      this.resetProcessing();
    },
    handleEditButtonResetError() {
      this.dataEditButton.loading = false;
      this.dataEditButton.error = false;
    },
    handleMaxValidationError() {
      this.dataSaveButton.errorMessage = this.getMaxSelectedErrorMessage();
      this.onFail();
    },
    handleEditValidationError() {
      this.dataEditButton.errorMessage = this.$i18n.t("lesson_template_subpage_edit_right_error");
      this.onEditFail();
    },
    handleEdit() {
      this.setProcessing("edit");
      if (!this.isAllowedEdit) {
        this.handleEditValidationError();
        return;
      }

      const lessonData = Object.assign({}, this.paramsData.lesson, {
        communityResourceUuid: this.paramsData.communityResourceUuid,
        assignmentUuid: this.paramsData.assignmentUuid,
      });

      this.resourceEditMixin_handleEditLesson(
        this.paramsData.type,
        lessonData,
        this.handleResponseCallback,
      );
    },
    handleAnchor(anchorId) {
      ScrollUtils.scrollToView(`#${anchorId}`);
    },
    processRubricResponse(response) {
      const rubricData = response.data;

      if (rubricData.createdDate) {
        rubricData.createdDate = DateTimeFormatter.formatLocalDateTimeToDateTimeAmPm(
          rubricData.createdDate,
        );
      }

      if (rubricData.modifiedDate) {
        rubricData.modifiedDate = DateTimeFormatter.formatLocalDateTimeToDateTimeAmPm(
          rubricData.modifiedDate,
        );
      }

      if (rubricData.createdBy) {
        rubricData.createdById = rubricData.createdBy.id;
        rubricData.createdBy = rubricData.createdBy.name;
      }

      if (rubricData.modifiedBy) {
        rubricData.modifiedById = rubricData.modifiedBy.id;
        rubricData.modifiedBy = rubricData.modifiedBy.name;
      }

      rubricData.criteriaList = rubricData.criteriaModelList.map((criteria) => ({
        id: criteria.id,
        description: criteria.description,
        indexNo: criteria.indexNo,
      }));

      // Build rangeList and criteriaRangeList from per-criterion structure (SLS-11945)
      rubricData.rangeList = [];
      rubricData.criteriaRangeList = [];

      if (rubricData.criteriaModelList) {
        rubricData.criteriaModelList.forEach((criteria) => {
          // Collect all ranges from each criterion
          if (criteria.rangeModelList) {
            rubricData.rangeList.push(
              ...criteria.rangeModelList.map((range) => ({
                id: range.id,
                description: range.description,
                indexNo: range.indexNo,
              })),
            );
          }

          // Collect all criteriaRanges from each criterion
          if (criteria.criteriaRangeModelList) {
            rubricData.criteriaRangeList.push(
              ...criteria.criteriaRangeModelList.map((criteriaRange) => ({
                gradingRubricCriteriaId: criteriaRange.gradingRubricCriteriaId,
                gradingRubricRangeId: criteriaRange.gradingRubricRangeId,
                criteriaIndex: criteriaRange.criteriaIndex,
                rangeIndex: criteriaRange.rangeIndex,
                maxScore: criteriaRange.maxScore,
                description: criteriaRange.description || "",
              })),
            );
          }
        });
      }

      rubricData.location = this.location(rubricData.resourceDomain);

      rubricData.type = RESOURCE_TYPE_VALUE.RUBRIC;
      rubricData.highlights = this.paramsData?.gradingRubric?.highlights ?? {};
      rubricData.notes = rubricData.description;

      return rubricData;
    },
  },
};
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.content-subpage.search-resource-detail-content-subpage {
  .bx--modal-container {
    > .header {
      .breadcrumb {
        .title {
          max-width: 100%;
        }
      }
    }

    > .content {
      > .sub {
        &:has(.search-resource-media-detail) {
          padding: 0;
        }
      }
    }
  }

  .media._has-status {
    margin: 0;
    border-radius: 10px;
  }

  .media {
    .attachment-placeholder {
      .star-resource-button {
        display: none;
      }
    }
  }

  .resource-taggings {
    .tagging {
      .bx--accordion__item {
        background-color: $ui-02;
      }
    }
  }

  .bx--form {
    &:first-child {
      margin-top: 0;
    }

    .field-set {
      &.read-only-indicator {
        .field-value {
          @include carbon--type-style("helper-text-01");
          display: flex;
          align-items: center;
          color: $text-02;
        }

        .sls-icon {
          min-width: 24px;
          margin-right: $spacing-03;

          [fill] {
            fill: $icon-02;
            &[fill="#DFE8F9"] {
              opacity: 0.15;
            }
          }

          [stroke] {
            stroke: $icon-02;
          }
        }
      }

      &.used-in {
        .lesson-detail {
          width: 100%;

          &:not(:first-of-type) {
            margin-top: $spacing-07;
          }
        }
      }

      &.view-count {
        .field-value {
          display: flex;
          align-items: center;

          .sls-icon {
            margin-right: $spacing-03;
          }
        }
      }
    }
  }

  .subject-and-level-datatable {
    .v-table__wrapper {
      thead {
        tr {
          th {
            width: 50%;
          }
        }
      }

      tbody {
        tr {
          td {
            span.main {
              display: block;
            }
          }
        }
      }
    }
  }
}
</style>
